/**
 * Konfiguracja menu panelu super-admina.
 *
 * F2 MVP: staticzna lista w kodzie.
 * Kierunek docelowy (F8+ / Etap 2):
 *   - Struktura ta sama, ale zaladowana z bazy (ADM tabela `admin_menu_config`
 *     albo per-tenant w `tenants.settings.menu` dla panelu klienta).
 *   - Super-admin / admin klienta edytuje przez UI (drag-and-drop, enable/disable
 *     pozycji, zmiana order, dodawanie customowych linkow).
 *   - Menu per-rola: inne dla super_admin, inne dla support, inne dla klienta.
 *
 * Dlatego struktura juz teraz JSON-serializowalna (zero funkcji,
 * tylko strings/bools/numbers) — zeby latwo wrzucic do bazy jako JSONB.
 *
 * ADR-0034 (admin menu builder): pole `requiredRoles?: AdminRole[]` filtruje
 * pozycje per rola admina. Pominięcie pola = widoczne dla wszystkich ról.
 * Implementacja w `Sidebar.tsx` `AdminSidebar` filtruje przez useAuth().admin.role.
 */

import type { AdminRole } from '@/contexts/AuthContext';

export type MenuItemStatus = 'active' | 'coming_soon';

export interface MenuItem {
  /** Klucz unikalny — uzywany w URL i jako React key */
  key: string;
  /** Etykieta widoczna w UI (i18n key w przyszlosci) */
  label: string;
  /** Ikona z biblioteki lucide-react */
  icon: string;
  /** Sciezka w react-router */
  path: string;
  /** Status pozycji */
  status: MenuItemStatus;
  /** Jesli coming_soon — w ktorej fazie bedzie gotowe (do tooltipa/toastu) */
  phase?: string;
  /** Kolejnosc w menu (mniejsza = wyzej) */
  order: number;
  /** Czy pozycja jest widoczna w sidebarze (false = ukryta totalnie). */
  visible: boolean;
  /**
   * Lista ról które MOGĄ zobaczyć tę pozycję. Pominięcie = wszystkie role widzą.
   * ADR-0034 — np. „Edytor menu" widoczny TYLKO dla `super_admin`.
   */
  requiredRoles?: AdminRole[];
  /**
   * Kod uprawnienia admina wymagany do zobaczenia pozycji (np. `db_servers.manage`).
   * Pominięcie = bez wymogu uprawnienia. Łączy się z `requiredRoles` (oba muszą przejść).
   */
  requiredPermission?: string;
}

/**
 * Menu panelu super-admina SaaS-a (admin.naszsaas.pl).
 *
 * Panel klienta (<slug>.app.naszsaas.pl) bedzie mial INNE menu — dorzucamy
 * w F5 (gdy powstanie ekran klienta). Panel klienta nie ma "Tenanci"
 * ani "Admin Users" — bo klient jest sam tenantem i nie widzi innych.
 */
export const ADMIN_MENU: MenuItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/dashboard',
    status: 'active',
    order: 10,
    visible: true,
  },
  {
    key: 'modules',
    label: 'Katalog modułów',
    icon: 'Package',
    path: '/modules',
    status: 'active',
    order: 20,
    visible: true,
  },
  {
    key: 'module-pricing',
    label: 'Cennik modułów',
    icon: 'BadgeDollarSign',
    path: '/module-pricing',
    status: 'active',
    order: 21,
    visible: true,
  },
  {
    key: 'bundles',
    label: 'Pakiety',
    icon: 'Package',
    path: '/bundles',
    status: 'active',
    order: 22,
    visible: true,
  },
  {
    key: 'db_pools',
    label: 'Pule baz',
    icon: 'Database',
    path: '/db-pools',
    status: 'active',
    order: 23,
    visible: true,
  },
  // Obszary nie mają osobnej pozycji — zarządzanie obszarami żyje w lewej kolumnie
  // Kreatora modułów (scalenie 2026-06-10; /module-areas przekierowuje tam).
  {
    key: 'module_templates',
    label: 'Kreator modułów',
    icon: 'LayoutTemplate',
    path: '/module-templates',
    status: 'active',
    order: 24,
    visible: true,
  },
  {
    key: 'kartoteki',
    label: 'Kartoteki',
    icon: 'Library',
    path: '/kartoteki',
    status: 'active',
    order: 25,
    visible: true,
  },
  {
    key: 'global_document_types',
    label: 'Globalne typy dokumentów',
    icon: 'FileStack',
    path: '/global-document-types',
    status: 'active',
    order: 26,
    visible: true,
  },
  {
    key: 'global_dictionaries',
    label: 'Globalne słowniki',
    icon: 'ListTree',
    path: '/global-dictionaries',
    status: 'active',
    order: 27,
    visible: true,
  },
  {
    key: 'chart_of_accounts_templates',
    label: 'Wzorce planu kont',
    icon: 'Landmark',
    path: '/chart-of-accounts-templates',
    status: 'active',
    order: 28,
    visible: true,
  },
  {
    key: 'fiscal_agent_releases',
    label: 'Usługa fiskalna',
    icon: 'Printer',
    path: '/fiscal-agent-releases',
    status: 'active',
    order: 29,
    visible: true,
  },
  {
    key: 'tenants',
    label: 'Tenanci',
    icon: 'Building2',
    path: '/tenants',
    status: 'active',
    order: 30,
    visible: true,
  },
  {
    key: 'subscriptions',
    label: 'Subskrypcje',
    icon: 'Receipt',
    path: '/subscriptions',
    status: 'active',
    order: 40,
    visible: true,
  },
  {
    key: 'invoices',
    label: 'Faktury',
    icon: 'FileText',
    path: '/invoices',
    status: 'active',
    order: 50,
    visible: true,
  },
  {
    key: 'coupons',
    label: 'Kupony',
    icon: 'Ticket',
    path: '/coupons',
    status: 'active',
    order: 60,
    visible: true,
  },
  {
    key: 'admin_users',
    label: 'Użytkownicy Panelu Admin',
    icon: 'ShieldCheck',
    path: '/admin-users',
    status: 'active',
    order: 70,
    visible: true,
  },
  {
    key: 'audit_log',
    label: 'Log zdarzeń',
    icon: 'ScrollText',
    path: '/audit-log',
    status: 'active',
    order: 80,
    visible: true,
  },
  // Zgłoszenia błędów od użytkowników tenantów (widget „Zgłoś błąd" po stronie tenanta).
  {
    key: 'bug_reports',
    label: 'Zgłoszenia',
    icon: 'Bug',
    path: '/bug-reports',
    status: 'active',
    order: 82,
    visible: true,
  },
  // F17 T17.11.5 — wzory procesów (centralna biblioteka szablonów dla tenantów).
  {
    key: 'process_templates',
    label: 'Szablony procesów',
    icon: 'GitBranch',
    path: '/process-templates',
    status: 'active',
    order: 85,
    visible: true,
  },
  // ADR-0034 — admin menu builder. Widoczne TYLKO dla super_admin
  // (edycja layoutów menu wpływa na to co reszta operatorów ITE widzi
  // w sidebarze; per ADR-0034 to kategoria privilegowana).
  {
    key: 'menu_builder',
    label: 'Edytor menu',
    icon: 'MenuSquare',
    path: '/menu-builder',
    status: 'active',
    order: 90,
    visible: true,
    requiredRoles: ['super_admin'],
  },
];
